import React, { useContext, useEffect, useState, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import styled, { ThemeContext } from "styled-components";
import { LoadingData } from "./_helpers/LoadingData";
import { useParams } from "react-router-dom";
import { MachineMetadataFC } from "../_helpers/displayFiles/MachineMetadataFC";
import { useAuth0 } from "@auth0/auth0-react";
import { _ChartIndex } from "../_helpers/charts/_ChartIndex";
import { _FusionTimeIndex } from "../_helpers/fusionCharts/_FusionTimeIndex";
import { UnifiedChart } from "../_helpers/UnifiedChart";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import { renderCount } from "../../../../appHelpers/appLoggers";
import { LevelSelectorFC } from "../_helpers/LevelSelectorFC";
// import { ChartSelectorFC } from "../_helpers/ChartSelectorFC"; // Replaced by UnifiedChart internal controls
import { Loader_MachineMetadataFC } from "../_helpers/displayFiles/loaders/Loader_MachineMetadataFC";
import { _MMBAlertFC } from "./_helpers/_MMBAlertFC";
import { FlexboxGrid, Message } from "rsuite";
import FlexboxGridItem from "rsuite/esm/FlexboxGrid/FlexboxGridItem";
import { MainDivider } from "../admin/helpers/Admin_StyledComponents";
import { DateTime } from "luxon";
import { MagnetHeader } from "./MagnetHeader";
import { UserContext } from "../_context/UserContext";
import { TableFC } from "./_helpers/TableFC";
import { getMessage } from "../_utils/util_general";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { InputContext } from "../_context/InputContext";
import { ExportMMB } from "../_helpers/export/ExportMMB";
import { TableSelector } from "../_helpers/TableSelectorFC";
import { SegmentedPicker, Button, GlassCard, colors } from "../../../ui";
import { SystemIssues } from "./_helpers/SystemIssues";

export const _SystemFC = () => {
  //renderCount('_SystemFC');
  const { user, setUser, userRoles, getUserProfile, loadingUserRoles } =
    useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();
  const [selectedDataView, setSelectedDataView] = useState(`CHARTS`);

  // Filter dataViewOptions based on user roles
  const dataViewOptions = useMemo(() => {
    const baseOptions = [
      { eventKey: "CHARTS", label: "Charts" },
      { eventKey: "TABLE", label: "Data Table" },
      { eventKey: "ALERTS", label: "Alert Config" },
    ];

    // Only show Issues tab for network and superuser roles
    const canViewIssues =
      Array.isArray(userRoles) &&
      userRoles.some((role) => role === "network" || role === "superuser");

    if (canViewIssues) {
      baseOptions.push({ eventKey: "ISSUES", label: "Issues" });
    }

    return baseOptions;
  }, [userRoles]); //`LOG`, When the time is right :P
  const [tableSwitch, setTableSwitch] = useState("COMBINED");

  // ADDED FIELDS
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [loadingDataTable, setLoadingDataTable] = useState(true);
  const [loadingDataConfig, setLoadingDataConfig] = useState(true);
  const [loadingAlertConfig, setLoadingAlertConfig] = useState(true);
  const [metadata, setMetadata] = useState([]);
  const [issues, setIssues] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [frozenData, setFrozenData] = useState([]);
  const [dataConfig, setDataConfig] = useState([]);
  const [alertConfig, setAlertConfig] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [getMetadataError, setGetMetadataError] = useState(false);
  const [getDataTableError, setGetDataTableError] = useState(false);
  const [getDataConfigError, setGetDataConfigError] = useState(false);
  const [getAlertConfigError, setGetAlertConfigError] = useState(false);
  const [level, setLevel] = useState(1);
  // const [chart, setChart] = useState(1); // No longer needed - UnifiedChart handles chart type internally
  const { id, dataModel } = useParams();
  const history = useHistory();
  const [activeKey, setActiveKey] = useState("1");

  const { viewToggle, setViewToggle } = useContext(InputContext);

  const getMachineMetadata = async () => {
    // console.log(`getMachineMetadata`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/metadata/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMetadata(response.data[0][0]);
    } catch (error) {
      console.log(error);
      setGetMetadataError(true);
    }
    setLoadingMetadata(false);
  };

  const getNetworkIssues = async () => {
    setLoadingIssues(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/issues`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setIssues(res?.data[0]);
    } catch (error) {
      console.error(error);
    }
    setLoadingIssues(false);
  };

  const getMachineDataTable = async () => {
    // console.log(`getMachineDataTable`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/data/${id}/0`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFrozenData(response.data[0].frozenData);
      setDataTable(cloneDeep(response.data));
      setChartData(cloneDeep(response.data));
    } catch (error) {
      setGetDataTableError(true);
      console.log(error);
    }
    setLoadingDataTable(false);
  };

  const getMachineDataConfig = async () => {
    // console.log(`getMachineDataConfig`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/config/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDataConfig(cloneDeep(response.data));
    } catch (error) {
      setGetDataConfigError(true);
      console.log(error);
    }
    setLoadingDataConfig(false);
  };

  const getSystemAlertConfig = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/alert/configs/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setAlertConfig(cloneDeep(response.data[0]));
    } catch (error) {
      setGetAlertConfigError(true);
      console.log(error);
    }
    setLoadingAlertConfig(false);
  };

  const ModernBreadcrumb = () => {
    return (
      <BreadcrumbContainer>
        <BreadcrumbSection>
          <Greeting>
            {user.first_name
              ? `GOOD ${getMessage()}, ${user.first_name?.toUpperCase()}!`
              : ""}
          </Greeting>
          <BreadcrumbPath>
            <Button
              variant="secondary"
              size="small"
              onClick={() => history.push(`/`)}
            >
              MAGNET MONITORING
            </Button>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <SystemLabel>{id}</SystemLabel>
          </BreadcrumbPath>
        </BreadcrumbSection>
        <UserSection>
          <ModernAvatar
            onClick={() => history.push(`/admin/user/${user.email_address}`)}
          >
            {(user.first_name ? user.first_name.charAt(0) : "") +
              " " +
              (user.last_name ? user.last_name.charAt(0) : "")}
          </ModernAvatar>
        </UserSection>
      </BreadcrumbContainer>
    );
  };

  useEffect(() => {
    getMachineMetadata();
    getMachineDataTable();
    getMachineDataConfig();
    getSystemAlertConfig();
    getNetworkIssues();
    getUserProfile();
    setViewToggle("MMB");
  }, []);

  useEffect(() => {}, []);

  const memoMetadata = useMemo(
    () => <MachineMetadataFC metadata={metadata} networkIssues={issues} />,
    [metadata],
  );

  const memoMagnetDataTable = useMemo(
    () => (
      <TableFC
        metadata={metadata}
        dataTable={dataTable}
        dataConfig={dataConfig}
        filterLevel={level}
        alertConfigs={alertConfig}
        tableSwitch={tableSwitch}
      />
    ),
    [dataTable, dataConfig, level, tableSwitch],
  );

  const memoMagnetChart = useMemo(() => {
    console.log("SystemFC Debug - Data for UnifiedChart:", {
      dataTable: dataTable?.length
        ? `Array[${dataTable.length}]`
        : "empty/undefined",
      dataTableStructure: dataTable?.[0]
        ? Object.keys(dataTable[0])
        : "no dataTable[0]",
      dataTableSample: dataTable?.[0]
        ? JSON.stringify(dataTable[0]).substring(0, 300)
        : "no data",
      dataConfig: dataConfig?.length
        ? `Array[${dataConfig.length}]`
        : "empty/undefined",
      metadata: metadata ? "present" : "undefined",
      alertConfig: alertConfig ? "present" : "undefined",
    });

    return (
      <UnifiedChart
        rawData={dataTable[0]}
        dataConfig={dataConfig}
        metadata={metadata}
        alertConfigs={alertConfig}
      />
    );
  }, [dataTable, dataConfig, metadata, alertConfig]);

  const memoAlertConfigTable = useMemo(
    () => (
      <_MMBAlertFC
        metadata={metadata}
        dataConfig={dataTable[0]?.chartConfig}
        dataTable={dataTable[0]?.dataTable[0]}
        alertConfigs={alertConfig}
        setAlertConfigs={setAlertConfig}
        refresh={getSystemAlertConfig}
      />
    ),
    [dataTable, dataConfig, alertConfig],
  );

  // CONTROLLING THE LOADING CONDITIONAL WORKS NICELY HERE
  // OTHERWISE, A CHECK IS NEEDED IN THE COMPONENT ITSELF FOR DESTRUCTORING
  // DATA FROM THE CONTEXT WHICH MAY OR MAY NOT HAVE LOADED YET
  const DataView = () => {
    switch (selectedDataView) {
      case `TABLE`:
        return loadingDataTable ||
          loadingDataConfig ||
          loadingAlertConfig ||
          loadingMetadata ? (
          <LoadingData />
        ) : !getDataTableError &&
          !getDataConfigError &&
          dataTable[0]?.dataTable.length > 0 ? (
          memoMagnetDataTable
        ) : dataTable[0]?.dataTable.length === 0 ? (
          <>No Data</>
        ) : (
          <>Something Went Wrong</>
        );
      case `CHARTS`:
        return loadingDataTable ||
          loadingDataConfig ||
          loadingAlertConfig ||
          loadingMetadata ? (
          <LoadingData />
        ) : !getDataTableError &&
          !getDataConfigError &&
          dataTable[0]?.dataTable.length > 0 ? (
          memoMagnetChart
        ) : dataTable[0]?.dataTable.length === 0 ? (
          <>No Data</>
        ) : (
          <>Something Went Wrong</>
        );
      case `ALERTS`:
        return loadingAlertConfig || loadingMetadata ? (
          <LoadingData />
        ) : !getAlertConfigError ? (
          memoAlertConfigTable
        ) : (
          <>Something Went Wrong</>
        );
      case `ISSUES`:
        return <SystemIssues systemId={id} metadata={metadata} />;
      default:
        return <div>NON-CONFORMANT STATE: RemoteFilesFC:DataView</div>;
    }
  };

  return (
    <SystemContainer>
      <ModernBreadcrumb />

      <ContentWrapper>
        {/* System Metadata */}
        {loadingMetadata && loadingIssues ? (
          <Loader_MachineMetadataFC />
        ) : (
          memoMetadata
        )}

        {/* Magnet Header */}
        {!loadingDataTable &&
          !loadingAlertConfig &&
          dataTable[0]?.dataTable.length > 0 && (
            <MagnetHeader
              metadata={metadata}
              magnetData={dataTable[0]}
              dataConfig={dataConfig}
              filterLevel={level}
              alertConfigs={alertConfig}
            />
          )}

        {/* System Issues Warning */}
        {issues?.filter((issue) => issue.id === metadata.id).length > 0 &&
          issues
            ?.filter((issue) => issue.id === metadata.id)
            .map((issue) =>
              !issue.resolved &&
              issue.status === "active" &&
              (issue.processor_type === "MMB" || issue.type === "MAGNET") ? (
                <WarningCard key={issue.id}>
                  <Message
                    showIcon
                    type="warning"
                    header="Warning"
                    closable
                    style={{ width: "100%" }}
                  >
                    This System is experiencing issues.
                    <MainDivider />
                    <FlexboxGrid>
                      <FlexboxGrid.Item colspan={5}>
                        Reported:{" "}
                        {DateTime.fromISO(issue.inserted_at).toFormat("ff")}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={3}>
                        Assigned: {issue.assigned}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={3}>
                        Type: {issue.type}
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item colspan={12}>
                        Notes: {issue.notes}
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  </Message>
                </WarningCard>
              ) : null,
            )}

        {/* Frozen Data Warnings */}
        {frozenData.map((frozen, index) => (
          <WarningCard key={index}>
            <Message
              showIcon
              type="error"
              header="Error"
              closable
              style={{ width: "100%" }}
            >
              This System has instances of FROZEN magnet data in the past 6
              hours.
              <MainDivider />
              <FlexboxGrid>
                <FlexboxGridItem colspan={3}>
                  Occurences: {frozen.max}
                </FlexboxGridItem>
              </FlexboxGrid>
              <FlexboxGrid>
                <FlexboxGridItem colspan={21}>
                  Occured Between:{" "}
                  {DateTime.fromISO(frozen.arr_capture_datetime[0]).toFormat(
                    "ff",
                  )}{" "}
                  -{" "}
                  {DateTime.fromISO(
                    frozen.arr_capture_datetime[
                      frozen.arr_capture_datetime.length - 1
                    ],
                  ).toFormat("ff")}
                </FlexboxGridItem>
              </FlexboxGrid>
            </Message>
          </WarningCard>
        ))}

        {/* Navigation and Controls */}
        <ModernControls $isIssuesView={selectedDataView === "ISSUES"}>
          <NavigationContainer>
            <SegmentedPicker
              options={dataViewOptions}
              activeKey={selectedDataView}
              onSelect={setSelectedDataView}
              variant="glass"
              size="medium"
            />
          </NavigationContainer>

          {selectedDataView !== "ISSUES" && (
            <ControlsRow>
              {/* ChartSelectorFC removed - UnifiedChart has built-in controls */}

              {selectedDataView !== "ALERTS" && (
                <LevelSelectorFC level={setLevel} />
              )}

              {metadata.manufacturer?.toUpperCase() === "PHILIPS" &&
                !loadingDataConfig &&
                selectedDataView === "TABLE" && (
                  <TableSelector
                    table={setTableSwitch}
                    tables={dataConfig[0]?.dataTables}
                  />
                )}

              {dataTable[0] &&
                !loadingDataConfig &&
                !loadingDataTable &&
                (selectedDataView === `CHARTS` ||
                  selectedDataView === `TABLE`) && (
                  <ExportMMB
                    systems={[metadata]}
                    recent_data={dataTable}
                    engineerLevel={level}
                    dataConfig={dataConfig}
                  />
                )}
            </ControlsRow>
          )}
        </ModernControls>

        {/* Data View Content */}
        <DataContainer>
          <DataView />
        </DataContainer>
      </ContentWrapper>
    </SystemContainer>
  );
};

// Modern Styled Components
const SystemContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 0 20px 40px 20px;
  color: ${colors.neutral[800]};

  @media screen and (max-width: 768px) {
    padding: 0 16px 24px 16px;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 0;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const BreadcrumbSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Greeting = styled.div`
  font-size: 0.9em;
  color: ${colors.neutral[600]};
  font-weight: 500;

  @media screen and (max-width: 768px) {
    font-size: 0.8em;
  }
`;

const BreadcrumbPath = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BreadcrumbSeparator = styled.span`
  color: ${colors.neutral[500]};
  font-size: 1.1em;
`;

const SystemLabel = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
`;

const ModernAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${colors.primary.main} 0%,
    ${colors.primary.dark} 100%
  );
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(44, 90, 160, 0.4);
  }

  @media screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  @media screen and (max-width: 768px) {
    gap: 16px;
  }
`;

const WarningCard = styled.div`
  margin: 16px 0;
`;

const ModernControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: ${({ $isIssuesView }) => ($isIssuesView ? "1px" : "3px")};
  margin: ${({ $isIssuesView }) => ($isIssuesView ? "1px 0" : "3px 0")};

  @media screen and (max-width: 768px) {
    padding: ${({ $isIssuesView }) => ($isIssuesView ? "1px" : "3px")};
    margin: ${({ $isIssuesView }) => ($isIssuesView ? "1px 0" : "3px 0")};
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const DataContainer = styled.div`
  width: 100%;
  margin-top: 16px;
  overflow-x: auto;
`;

// Legacy Controls (keeping for compatibility)
const Controls = styled.div`
  display: flex;
`;
